import { Box, Typography, CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, List, ListItem, ListItemText, Divider } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useRecordContext } from 'react-admin';
import { Fragment, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import constants from '../../constants';
import moment from 'moment';
import { authHeaders } from '../Banner/BannerImagePicker';

// GH-770 — this tab used to download every login the company ever had (with the user of each) as soon as
// the company page opened: react-admin keeps hidden tabs mounted. Now it loads only once the tab is actually
// shown, gets per-month counts for the last 12 months, and loads a month's users only when its bar is clicked.

type MonthStat = { yearMonth: string; logins: number; users: number };
type MonthUser = { userId: string; first_name: string; last_name: string; email: string; logins: number };
type ChartData = { month: string; monthKey: string; logins: number; users: number };

/** true from the first moment the element is really on screen (a hidden tab is display:none → never intersects). */
const useShownOnce = <T extends Element>() => {
	const ref = useRef<T>(null);
	const [shown, setShown] = useState(false);
	useEffect(() => {
		if (shown || !ref.current) return;
		if (typeof IntersectionObserver === 'undefined') {
			setShown(true);
			return;
		}
		const observer = new IntersectionObserver((entries) => {
			if (entries.some((e) => e.isIntersecting)) {
				setShown(true);
				observer.disconnect();
			}
		});
		observer.observe(ref.current);
		return () => observer.disconnect();
	}, [shown]);
	return { ref, shown };
};

const LoginHistory = () => {
	const record = useRecordContext();
	const { ref, shown } = useShownOnce<HTMLDivElement>();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [chartData, setChartData] = useState<ChartData[]>([]);
	const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [monthUsers, setMonthUsers] = useState<Record<string, MonthUser[]>>({});
	const [monthLoading, setMonthLoading] = useState(false);
	const [monthError, setMonthError] = useState<string | null>(null);
	const currentMonth = useRef<string | null>(null); // a late reply for another month must not touch this month's spinner/error

	useEffect(() => {
		if (!shown) return;
		if (!record?._id) {
			setLoading(false);
			return;
		}
		let cancelled = false;
		const fetchLoginStats = async () => {
			try {
				setLoading(true);
				setError(null);
				setMonthUsers({});
				const response = await axios.post<{ months: MonthStat[] }>(
					`${constants.API_URL}/company/login-stats`,
					{ companyId: record._id },
					{ headers: authHeaders() }
				);
				if (cancelled) return;
				setChartData(
					(response.data.months || []).map((m) => ({
						month: moment(m.yearMonth, 'YYYY-MM').format('MM/YYYY'),
						monthKey: m.yearMonth,
						logins: m.logins,
						users: m.users,
					}))
				);
			} catch (err) {
				console.error('Error fetching login history:', err);
				if (!cancelled) setError('שגיאה בטעינת היסטוריית התחברות');
			} finally {
				if (!cancelled) setLoading(false);
			}
		};
		fetchLoginStats();
		return () => {
			cancelled = true;
		};
	}, [shown, record?._id]);

	const openMonth = async (monthKey: string) => {
		currentMonth.current = monthKey;
		setSelectedMonth(monthKey);
		setDialogOpen(true);
		setMonthError(null);
		if (monthUsers[monthKey] || !record?._id) return;
		try {
			setMonthLoading(true);
			const response = await axios.post<{ users: MonthUser[] }>(
				`${constants.API_URL}/company/login-stats/month`,
				{ companyId: record._id, yearMonth: monthKey },
				{ headers: authHeaders() }
			);
			setMonthUsers((prev) => ({ ...prev, [monthKey]: response.data.users || [] }));
		} catch (err) {
			console.error('Error fetching month logins:', err);
			if (currentMonth.current === monthKey) setMonthError('שגיאה בטעינת רשימת המתחברים');
		} finally {
			if (currentMonth.current === monthKey) setMonthLoading(false);
		}
	};

	const selectedStat = chartData.find((d) => d.monthKey === selectedMonth);
	const selectedUsers = selectedMonth ? monthUsers[selectedMonth] : undefined;

	let content;
	if (loading) {
		content = (
			<Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
				<CircularProgress />
			</Box>
		);
	} else if (error) {
		content = <Alert severity="error">{error}</Alert>;
	} else if (chartData.length === 0) {
		content = (
			<>
				<Typography variant="h6" gutterBottom>
					היסטורית התחברות
				</Typography>
				<Typography>אין נתוני התחברות ב-12 החודשים האחרונים</Typography>
			</>
		);
	} else {
		content = (
			<>
				<Typography variant="h6" gutterBottom>
					היסטורית התחברות לפי חודשים (12 החודשים האחרונים)
				</Typography>
				<Typography variant="body2" color="text.secondary" gutterBottom>
					לחץ על עמודה כדי לראות את רשימת המתחברים
				</Typography>
				<Box sx={{ width: '100%', height: 600, mt: 3 }}>
					<ResponsiveContainer width="100%" height="100%">
						<BarChart
							data={chartData}
							margin={{
								top: 20,
								right: 30,
								left: 20,
								bottom: 0,
							}}
							onClick={(data: any) => {
								const clickedData = data?.activePayload?.[0]?.payload;
								if (clickedData?.monthKey) openMonth(clickedData.monthKey);
							}}
						>
							<CartesianGrid strokeDasharray="3 3" />
							<XAxis dataKey="month" angle={0} textAnchor="end" height={150} dy={20} dx={-30} />
							<YAxis />
							<Tooltip />
							<Legend />
							<Bar dataKey="logins" fill="#8884d8" name="מספר התחברויות" style={{ cursor: 'pointer' }} />
						</BarChart>
					</ResponsiveContainer>
				</Box>
			</>
		);
	}

	return (
		<Box ref={ref} sx={{ width: '100%', p: 3 }}>
			{content}

			<Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth dir="rtl">
				<DialogTitle>מתחברים בחודש {selectedMonth ? moment(selectedMonth, 'YYYY-MM').format('MM/YYYY') : ''}</DialogTitle>
				<DialogContent>
					{selectedStat && (
						<Typography variant="body2" color="text.secondary" gutterBottom>
							סה"כ {selectedStat.logins} התחברויות מ-{selectedStat.users} משתמשים ייחודיים
						</Typography>
					)}
					<Divider sx={{ my: 2 }} />
					{monthLoading && !selectedUsers ? (
						<Box display="flex" justifyContent="center" py={4}>
							<CircularProgress />
						</Box>
					) : monthError && !selectedUsers ? (
						<Alert severity="error">{monthError}</Alert>
					) : (
						<List>
							{(selectedUsers || []).map((u, index) => (
								<Fragment key={u.userId}>
									<ListItem>
										<ListItemText
											primary={`${u.email ? `${u.first_name} ${u.last_name}`.trim() || u.email : 'משתמש שנמחק'} (${u.logins})`}
											secondary={u.email || u.userId}
										/>
									</ListItem>
									{index < (selectedUsers || []).length - 1 && <Divider />}
								</Fragment>
							))}
						</List>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDialogOpen(false)}>סגור</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default LoginHistory;
